import { Control, FieldValues, Path } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import {
  MultiSelectOption,
  MultiSelectWithPagination,
} from '@/modules/common/components/molecules/selectors/multiselect-with-pagination';
import { useProjectSelectors } from '@/modules/projects/hooks/info/use-project-selectors';

export type ManagerSelectItem = MultiSelectOption;

const MANAGERS_QUERY_KEY = ['team-members-short', 'managers'];

interface ManagersSelectProps<T extends FieldValues> {
  name: Path<T>;
  control: Control<T>;
  label?: string;
  placeholder?: string;
  disabled?: boolean;
  /**
   * Pre-populated options (e.g. currently selected managers including deleted ones).
   * Merged with API-loaded options so selected values always display correctly.
   */
  initialOptions?: ManagerSelectItem[];
  /**
   * Called when the selected items change, with the full item objects.
   * Useful for tracking display names (e.g. for conflict resolution modal).
   */
  onSelectItems?: (items: ManagerSelectItem[]) => void;
}

/**
 * Multi-select for project managers with avatar chip support and cursor-based pagination.
 * Lazily loads managers from the API on first open, with infinite scroll and debounced search.
 */
export function ManagersSelect<T extends FieldValues>({
  name,
  control,
  label,
  placeholder,
  disabled = false,
  initialOptions = [],
  onSelectItems,
}: ManagersSelectProps<T>) {
  const { t } = useTranslation(['common']);
  const { loadManagers, searchManagers } = useProjectSelectors();

  const handleLoadItems = ({
    searchString,
    cursor,
  }: {
    searchString?: string;
    cursor: number | null;
  }) => {
    if (searchString) {
      return searchManagers(searchString, cursor);
    }
    return loadManagers(cursor);
  };

  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => (
        <FormItem>
          {label && <FormLabel>{label}</FormLabel>}
          <MultiSelectWithPagination
            queryKey={MANAGERS_QUERY_KEY}
            loadItems={handleLoadItems}
            initialOptions={initialOptions}
            value={field.value || []}
            onValueChange={(values, selectedItems) => {
              field.onChange(values);
              onSelectItems?.(selectedItems);
            }}
            placeholder={placeholder ?? t('common:select')}
            disabled={disabled}
            className="w-full disabled:pointer-events-none disabled:cursor-not-allowed disabled:border-input disabled:bg-muted disabled:opacity-100"
          />
          <FormMessage />
        </FormItem>
      )}
    />
  );
}
